import { Suspense, useContext, lazy } from 'react';
import { ObjectPageContext } from '../../../pages/Context/ObjectPageContext';

const RoomDescAccordion = lazy(() => import('./RoomDescAccordion'));
const AssociationAccordion = lazy(() => import('./AssociationAccordion'));
const PlotInfo = lazy(() => import('./PlotInfo'));
const AboutEstateAccordion = lazy(() => import('./AboutEstateAccordion'));
const LandAccordion = lazy(() => import('./LandAccordion'));
const AboutAreaAccordion = lazy(() => import('./AboutAreaAccordion'));
const EconomyAccordion = lazy(() => import('./EconomyAccordion'));
const DocumentAccordion = lazy(() => import('./DocumentAccordion'));

import moment from 'moment';
import 'moment/locale/sv';

import { EstateType } from '../../../Enums';
import { Association, Economy, General } from '~js/React/ObjectPageEnums';

export const ObjectAccordion = (): JSX.Element => {
  const { estate: data } = useContext(ObjectPageContext);
  const associationInfo = data?.associationInfo;

  const associationInfoArray =
    associationInfo && Object.values(associationInfo);
  const isEmpty = (value: unknown): boolean =>
    value === null ||
    value === 0 ||
    (Array.isArray(value) && value.length === 0); //check if array is empty

  const isCommercial = data?.estateTypeAsString == EstateType.Commercial;
  const isProject = data?.estateTypeAsString == EstateType.Project;
  const isFarm = data?.estateTypeAsString == EstateType.Agriculture;
  const isPlot = data?.estateTypeAsString == EstateType.Plot;
  moment.locale('sv');

  return (
    <div
      id="facts"
      className="m-auto my-6 px-0 lg:px-4 xxl:px-10 lg:my-16 flex flex-col gap-1"
    >
      {data?.addressInfo?.streetAddress &&
      data?.addressInfo?.streetAddress !== '-' &&
      data?.estateTypeAsString !== EstateType.Project ? (
        <div className="hidden md:block font-icb text-3xl mb-4">
          {data?.addressInfo?.streetAddress}
        </div>
      ) : null}
      {data?.projectName && data?.estateTypeAsString == EstateType.Project && (
        <div className="hidden md:block font-icb text-3xl mb-4">
          {data?.projectName}
        </div>
      )}

      {/*//? RUMSBESKRIVNING ===================== */}
      {data?.estateTypeAsString !== EstateType.Plot &&
      ((data?.rooms && data?.rooms?.length > 0) ||
        data?.otherResidentialBuildings?.length ||
        data?.propertyBuildings?.length ||
        data?.mainResidentialBuilding?.name) ? (
        <Suspense fallback={<></>}>
          <RoomDescAccordion
            isFarm={isFarm}
            isCommercial={isCommercial}
            label={`${
              isCommercial || isFarm
                ? General.Description
                : General.RoomDescription
            }`}
            id="description"
          />
        </Suspense>
      ) : null}

      {/*//? BOSTADEN/BESKRIVNING ===================== */}
      {isPlot ? (
        <Suspense fallback={<></>}>
          <PlotInfo label={General.AboutThePlot} id="description" />
        </Suspense>
      ) : (
        <Suspense fallback={<></>}>
          <AboutEstateAccordion
            isCommercial={isCommercial}
            isProject={isProject}
            label={`${
              isCommercial || isFarm
                ? General.AboutTheProperty
                : General.AboutTheHome
            }`}
            id="description"
          />
        </Suspense>
      )}

      {/* //? MARK ================= */}
      {((data?.lands && data?.lands.length > 0) ||
        (data?.arealData && data?.arealData.length > 0)) && (
        <Suspense fallback={<></>}>
          <LandAccordion label={General.Land} id="lands" />
        </Suspense>
      )}

      {/*//? FÖRENINGEN ===================== */}
      {((associationInfo && !associationInfoArray?.every(isEmpty)) ||
        associationInfo) && (
        <Suspense fallback={<></>}>
          <AssociationAccordion
            associationInfo={associationInfo}
            label={Association.Association}
            id="housing"
            wayDescription={data?.descriptions?.wayDescription}
          />
        </Suspense>
      )}
      {/* OMRÅDET */}
      {data?.addressInfo?.area &&
        (data?.surrounding?.generalAboutArea ||
          data?.surrounding?.communication ||
          data?.surrounding?.nearService ||
          data?.surrounding?.parking ||
          data?.surrounding?.other) && (
          <Suspense fallback={<></>}>
            <AboutAreaAccordion
              label={`Om ${
                data?.addressInfo?.area ? data?.addressInfo?.area : 'området'
              }`}
              id="area"
            />
          </Suspense>
        )}
      {/*//? EKONOMI ===================== */}
      {!isProject ? (
        <Suspense fallback={<></>}>
          <EconomyAccordion
            isFarm={isFarm}
            isCommercial={isCommercial}
            label={Economy.Economy}
            id="economy"
          />
        </Suspense>
      ) : null}

      {/*//? DOKUMENT & LÄNKAR ===================== */}
      {(data?.documents.length ||
        associationInfo?.documents.length > 0 ||
        data?.links.length > 0) && (
        <Suspense fallback={<></>}>
          <DocumentAccordion label={General.DocumentAndLinks} id="documents" />
        </Suspense>
      )}
    </div>
  );
};

export default ObjectAccordion;
